import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';
import NextImage from 'next/image';
import { useRef } from 'react';

import GardenMap from '@/assets/images/garden-map.webp';
import { Layout } from '@/components/Layout';
import { PointWithData } from '@/components/PointWithData';
import { ContentWithSidebar } from '@/components/shared/ContentWithSidebar';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { PageHeading } from '@/components/shared/PageHeading';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import getPageBanner from '@/helpers/getPageBanner';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { usePageSeo } from '@/hooks/usePageSeo';
import { COMMON_LOCALE_PART } from '@/locale';
import { IInteractiveMapPlant, IPage, IPageDetailsProps, ITicketSectionInfo, Locale, SiteMenuItem } from '@/types';

interface IInteractiveMapPageProps extends IPageDetailsProps {
  plants: IInteractiveMapPlant[];
}

export default function InteractiveMap({ siteMenu, pageDetails, plants }: IInteractiveMapPageProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const { seoOptions } = usePageSeo(pageDetails);

  if (!pageDetails || !plants) {
    return <EmptyContent />;
  }

  return (
    <Layout siteMenu={siteMenu} seoOptions={seoOptions} banner={getPageBanner(pageDetails)}>
      <ContentWithSidebar siteMenu={siteMenu}>
        <PageHeading>{pageDetails.name}</PageHeading>

        <div className="w-100 relative" ref={mapContainerRef}>
          <NextImage
            loading="lazy"
            className="ml-auto"
            src={GardenMap.src}
            width={GardenMap.width}
            height={GardenMap.height}
            alt="gargen_image"
          />
          {plants.map((plant) => (
            <PointWithData key={plant.id} plant={plant} />
          ))}
        </div>
      </ContentWithSidebar>
    </Layout>
  );
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: false,
    paths: getI18nPaths(),
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { locale } = params as IParams;

  const request = api({ locale: locale });

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<IPage>('pages/detail/interactive-map'),
    request<ITicketSectionInfo[]>('plants/get_all_locations'),
  ]);

  const values = responses.map((val) => (val.status === 'fulfilled' ? val.value.data : undefined));
  const [siteMenu, pageDetails, plants] = values;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      pageDetails,
      plants,
    },
  };
};
